import { useEffect, useRef, useState } from 'react';
import propTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { format, isValid, add } from 'date-fns';
import 'react-day-picker/lib/style.css';
import DayPickerInput from 'react-day-picker/DayPickerInput';
import { formatDate, parseDate } from 'react-day-picker/moment';
import ClickAwayListener from '@material-ui/core/ClickAwayListener';
import ArrowLeftIcon from '@material-ui/icons/KeyboardArrowLeft';
import ArrowRightIcon from '@material-ui/icons/KeyboardArrowRight';
import ArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import DoubleArrowLeftIcon from 'assets/images/double-arrow-left.svg';
import DoubleArrowRightIcon from 'assets/images/double-arrow-right.svg';
import Button from '@material-ui/core/Button';
import cs from 'classnames';
import { getWeekStartDayNumO } from 'helpers/_helpers';
import { GLOBAL_DATE_FORMAT, globalBEDateFormat, momentAdepter } from 'common/dateFormatConfig';

import './styles.css';

const DayWeekPicker = ({
  classes,
  selectedDate,
  disabled,
  updateSelectedDate,
  weekly,
}) => {
  const [open, setOpen] = useState(false);
  const ref = useRef();
  const weekStart = useSelector((store) => store.settings?.settings?.weekStart || 'Sun');

  const date = isValid(selectedDate) ? selectedDate : new Date();
  const isToday = format(date, globalBEDateFormat) === format(new Date(), globalBEDateFormat);

  const changeDate = (next) => {
    if (isValid(next) && format(next, globalBEDateFormat) !== format(date, globalBEDateFormat)) {
      updateSelectedDate(next);
    }
  };

  useEffect(() => {
    if (open) ref.current.showDayPicker();
    else ref.current.hideDayPicker();
  }, [ref, open])

  const setToday = () => changeDate(new Date());

  const handleDayClick = (day) => {
    changeDate(day);
    setOpen(false);
  }
  const changeDay = (amount) => () => changeDate(add(date, { days: amount }));

  const closePicker = () => setOpen(false);
  const togglePicker = () => setOpen(!open);

  const renderDate = () => {
    if (weekly) {
      const end = add(date, { days: 7 });
      return `${format(date, 'd')}-${format(end, 'd')} ${format(end, GLOBAL_DATE_FORMAT.fullMonthYear)}`
    }
    return format(date, GLOBAL_DATE_FORMAT.fullDateWithDay);
  }

  return (
    <div className={cs('inlineFlexWrapper')}>
      <Button
        variant="contained"
        color="secondary"
        onClick={setToday}
        disabled={isToday}
        disableTouchRipple
      >
        Today
      </Button>
      {weekly ? (
        <img
          src={DoubleArrowLeftIcon}
          className={cs('changeMonthIcon leftArrow', { 'disabled': disabled })}
          onClick={disabled ? () => {} : changeDay(-7)}
          alt="double-arrow-left"
        />
      ) : null}
      <ArrowLeftIcon
        className={cs(
          'changeMonthIcon leftArrow',
          { 'disabled': disabled, 'marginZero': weekly }
        )}
        onClick={disabled ? () => {} : changeDay(-1)}
      />
      <ArrowRightIcon
        className={cs(
          'changeMonthIcon rightArrow',
          { 'disabled': disabled, 'marginZero': weekly }
        )}
        onClick={disabled ? () => {} : changeDay(1)}
      />
      {weekly ? (
        <img
          src={DoubleArrowRightIcon}
          className={cs('changeMonthIcon rightArrow', { 'disabled': disabled })}
          onClick={disabled ? () => {} : changeDay(7)}
          alt="double-arrow-right"
        />
      ) : null}
      <ClickAwayListener onClickAway={closePicker}>
        <div className="dayPickerWrapper endDatePicker fullWidth">
          <div className={cs("selectedDayName", { 'week': weekly })} onClick={togglePicker}>
            {renderDate()} <ArrowDownIcon className={cs('downArrow')} />
          </div>
          <DayPickerInput
            ref={ref}
            formatDate={formatDate}
            parseDate={parseDate}
            format={momentAdepter(GLOBAL_DATE_FORMAT.fullDateWithDay).momentShortDayDate}
            value={date}
            onDayChange={handleDayClick}
            dayPickerProps={{
              firstDayOfWeek: getWeekStartDayNumO(weekStart),
            }}
          />
        </div>
      </ClickAwayListener>
    </div>
  );
};

DayWeekPicker.propTypes = {
  selectedDate: propTypes.instanceOf(Date).isRequired,
  updateSelectedDate: propTypes.func.isRequired,
  disabled: propTypes.bool,
  weekly: propTypes.bool,
}

export default DayWeekPicker;
